import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  VisibilityOutlined as VisibilityIcon,
} from '@mui/icons-material';
import type { MaterialTypeSchema } from '../../types/recycling.type';
import { getMaterialCategoryTheme } from '../../constants/material-category-theme';
import { SetupCoachmark } from '../selfSetup/SetupCoachmark';

export interface MaterialCategoryGroup {
  categoryKey: string;
  label: string;
  items: MaterialTypeSchema[];
}

interface MaterialCategoryTableProps {
  group: MaterialCategoryGroup;
  formatLabel: (value: string) => string;
  onView: (material: MaterialTypeSchema) => void;
  onEdit: (material: MaterialTypeSchema) => void;
  onDelete: (material: MaterialTypeSchema) => void;
  /** Knowledge Base "Go to Edit or delete" — first row of first group. */
  helpTarget?: boolean;
  helpRowActionsPhase?: 'edit' | 'delete' | null;
  onHelpRowActionsNext?: () => void;
  onHelpRowActionsDismiss?: () => void;
}

const MaterialCategoryTable: React.FC<MaterialCategoryTableProps> = ({
  group,
  formatLabel,
  onView,
  onEdit,
  onDelete,
  helpTarget = false,
  helpRowActionsPhase = null,
  onHelpRowActionsNext,
  onHelpRowActionsDismiss,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { color, Icon } = getMaterialCategoryTheme(group.categoryKey, theme);

  const headCellSx = {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    py: 1.25,
    borderBottom: `1px solid ${theme.palette.divider}`,
    bgcolor: alpha(theme.palette.background.default, 0.4),
  };

  const bodyCellSx = {
    py: 1.25,
  };

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: '12px',
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(color, 0.12),
            color,
            flexShrink: 0,
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600, m: 0 }}>
          {group.label}
        </Typography>
      </Box>

      <TableContainer sx={{ flex: 1 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>{t('common.name')}</TableCell>
              <TableCell sx={{ ...headCellSx, width: 88 }}>{t('common.unit')}</TableCell>
              <TableCell align="right" sx={{ ...headCellSx, width: 120, px: 1 }}>
                {t('common.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {group.items.map((material, index) => {
              const isHelpTargetRow = helpTarget && index === 0 && Boolean(helpRowActionsPhase);

              return (
                <TableRow
                  key={material.id}
                  sx={{
                    '&:last-child td': { borderBottom: 0 },
                    '&:hover': { bgcolor: theme.palette.action.hover },
                    bgcolor:
                      index % 2 === 1
                        ? alpha(theme.palette.background.default, 0.35)
                        : 'transparent',
                  }}
                >
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>
                    {material.name}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, color: 'text.secondary' }}>
                    {material.unit_of_measure
                      ? formatLabel(material.unit_of_measure)
                      : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ width: 120, px: 0.5, py: 0.5, whiteSpace: 'nowrap' }}>
                    <Tooltip title={t('common.viewDetails')}>
                      <IconButton
                        size="small"
                        aria-label={t('common.viewDetails')}
                        onClick={() => onView(material)}
                        sx={{
                          color: theme.palette.text.secondary,
                          '&:hover': {
                            bgcolor: alpha(theme.palette.text.primary, 0.08),
                            color: theme.palette.text.primary,
                          },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {isHelpTargetRow && helpRowActionsPhase === 'edit' ? (
                      <SetupCoachmark
                        open
                        title={t('materialTypes.editMaterialHintTitle')}
                        subtitle={t('materialTypes.editMaterialHint')}
                        nextLabel={t('selfSetup.tourNext')}
                        onNext={() => onHelpRowActionsNext?.()}
                        onDismiss={() => onHelpRowActionsDismiss?.()}
                      >
                        <Tooltip title={t('common.edit')}>
                          <IconButton
                            size="small"
                            aria-label={t('common.edit')}
                            onClick={() => onEdit(material)}
                            sx={{
                              color: theme.palette.primary.main,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </SetupCoachmark>
                    ) : (
                      <Tooltip title={t('common.edit')}>
                        <IconButton
                          size="small"
                          aria-label={t('common.edit')}
                          onClick={() => onEdit(material)}
                          sx={{
                            color: theme.palette.primary.main,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {isHelpTargetRow && helpRowActionsPhase === 'delete' ? (
                      <SetupCoachmark
                        open
                        title={t('materialTypes.deleteMaterialHintTitle')}
                        subtitle={t('materialTypes.deleteMaterialHint')}
                        nextLabel={t('selfSetup.tourDone')}
                        onNext={() => onHelpRowActionsDismiss?.()}
                        onDismiss={() => onHelpRowActionsDismiss?.()}
                      >
                        <Tooltip title={t('common.delete')}>
                          <IconButton
                            size="small"
                            aria-label={t('common.delete')}
                            onClick={() => onDelete(material)}
                            sx={{
                              color: theme.palette.error.main,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.12),
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </SetupCoachmark>
                    ) : (
                      <Tooltip title={t('common.delete')}>
                        <IconButton
                          size="small"
                          aria-label={t('common.delete')}
                          onClick={() => onDelete(material)}
                          sx={{
                            color: theme.palette.error.main,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.error.main, 0.12),
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default MaterialCategoryTable;
